import { useEffect, useState } from 'react'

type AppEnv = 'development' | 'staging' | 'production'

/**
 * Non-production environment indicator. Renders a small fixed badge pinned to
 * the top-centre of every page so test data can never be mistaken for real
 * prescription data (the reason this exists) without a full-width strip getting
 * in the way. The full warning is preserved as the badge's hover title.
 * Production renders nothing.
 *
 * `appEnv` comes from the server (window.ENV.APP_ENV). On top of it we apply a
 * hostname safety fallback: if the server says "production" but we're served
 * from a *.fly.dev host, a deploy almost certainly forgot to set APP_ENV — flag
 * it as unconfirmed staging rather than showing nothing. A real custom prod
 * domain never trips this, so prod stays clean.
 */
export function EnvBanner({ appEnv }: { appEnv?: AppEnv }) {
	// Resolve on the client so we can read the hostname; start from the server
	// value to avoid a hydration flash on non-prod.
	const [env, setEnv] = useState<AppEnv | 'staging-unconfirmed'>(
		appEnv ?? 'production',
	)

	useEffect(() => {
		const host = window.location.hostname
		if (appEnv === 'production' && host.endsWith('.fly.dev')) {
			setEnv('staging-unconfirmed')
		} else {
			setEnv(appEnv ?? 'production')
		}
	}, [appEnv])

	if (env === 'production') return null

	// TEMPORARILY OPAQUE. The visible frame + label are disabled at Jacob's
	// request; the env is exposed only to DevTools via a data attribute on a
	// zero-size hidden node and a one-shot console.info tagged [env-banner].
	// Look up: document.querySelector('[data-app-env]').dataset.appEnv
	// Restore by reverting this file — the visible banner is the compliance
	// signal (see the removed comment: "must keep shouting in whatever palette
	// we adopt next").
	return (
		<div
			hidden
			data-app-env={env}
			ref={(node) => {
				if (node && !node.dataset.logged) {
					node.dataset.logged = '1'
					// eslint-disable-next-line no-console
					console.info(`[env-banner] APP_ENV=${env}`)
				}
			}}
		/>
	)
}
